import * as React from 'react'
import {
  useIsLoggedInQuery as useIsLoggedInQueryBase,
  useUserQuery as useUserQueryBase,
} from 'auth/hooks'
import { usePaymentsDialog as usePaymentsDialogBase } from 'payments/components/payments-dialog-wrapper-context'

import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint as savePaymentModalEntryPointBase,
} from 'payments/payments-dialog/analytics'
import { sessionStore as sessionStoreBase } from 'utils/storage'

export const PAYMENT_NUDGE_STORAGE_KEY = 'cfPaymentNudgeShown'

export interface PaymentNudgeDependencies {
  savePaymentModalEntryPoint: typeof savePaymentModalEntryPointBase
  sessionStore: Pick<typeof sessionStoreBase, 'getString' | 'setString'>
  useIsLoggedInQuery: typeof useIsLoggedInQueryBase
  usePaymentsDialog: typeof usePaymentsDialogBase
  useUserQuery: typeof useUserQueryBase
}

const defaultPaymentNudgeDependencies: PaymentNudgeDependencies = {
  savePaymentModalEntryPoint: savePaymentModalEntryPointBase,
  sessionStore: sessionStoreBase,
  useIsLoggedInQuery: useIsLoggedInQueryBase,
  usePaymentsDialog: usePaymentsDialogBase,
  useUserQuery: useUserQueryBase,
}

function Nudge({ dependencies }: { dependencies: PaymentNudgeDependencies }) {
  const { savePaymentModalEntryPoint, sessionStore, usePaymentsDialog } = dependencies
  const isNavigatingRef = React.useRef(false)
  const setNavigating = (value: boolean) => {
    isNavigatingRef.current = value // Use a ref instead of state to avoid stale closure issue
  }

  const { open: openPayments } = usePaymentsDialog()
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!sessionStore.getString(PAYMENT_NUDGE_STORAGE_KEY) && !isNavigatingRef.current) {
        sessionStore.setString(PAYMENT_NUDGE_STORAGE_KEY, 'true')

        if (document.querySelector('[data-payments-dialog]')) {
          return
        }

        savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.AUTO_TRIGGERED)
        if (window.cfOpenPaymentsDialog) {
          window.cfOpenPaymentsDialog({ useCase: 'nudge' })
        } else {
          openPayments(undefined, 'nudge')
        }
      }
    }, 5000)

    return () => clearTimeout(timeout)
  }, [openPayments, savePaymentModalEntryPoint, sessionStore])

  React.useEffect(() => {
    const handleNavigation = () => {
      setNavigating(true)
    }
    window.addEventListener('popstate', handleNavigation)
    window.addEventListener('beforeunload', handleNavigation)

    return () => {
      window.removeEventListener('popstate', handleNavigation)
      window.removeEventListener('beforeunload', handleNavigation)
    }
  }, [])

  return null
}

export function PaymentNudge({
  dependencies = defaultPaymentNudgeDependencies,
}: {
  dependencies?: PaymentNudgeDependencies
} = {}) {
  const { useIsLoggedInQuery, useUserQuery } = dependencies
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: hasAllAccess, isPending } = useUserQuery(undefined, {
    select(data) {
      return data.me?.user?.subscriptionAccess.allAccess.isSubscribed ?? false
    },
  })

  if (!isLoggedIn || isPending) {
    return null
  }

  if (!hasAllAccess) {
    return <Nudge dependencies={dependencies} />
  }

  return null
}
